import React, { useState } from "react";
import "../../css/Signup.css";
import logo from "../../components/images/logo-transparent-png.png";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const SignupPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    password: "",
    password2: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const states = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming"
  ];

  const navigate = useNavigate();
  const apiUrl = `${process.env.REACT_APP_API}`;

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidZipCode = (zipCode) => {
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;
    return zipCodeRegex.test(zipCode);
  };

  const isValidPhoneNumber = (phoneNumber) => {
    const phoneNumberRegex = /^\d{10}$/;
    return phoneNumberRegex.test(phoneNumber);
  };

  const isValidPassword = (password) => {
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    return passwordRegex.test(password);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case "firstName":
        if (!value.trim()) {
          newErrors.firstName = "First name is required";
        } else {
          delete newErrors.firstName;
        }
        break;
      case "lastName":
        if (!value.trim()) {
          newErrors.lastName = "Last name is required";
        } else {
          delete newErrors.lastName;
        }
        break;
      case "email":
        if (!value.trim()) {
          newErrors.email = "Email is required";
        } else if (!isValidEmail(value)) {
          newErrors.email = "Please enter a valid email address";
        } else {
          delete newErrors.email;
        }
        break;
      case "streetAddress1":
        if (!value.trim()) {
          newErrors.streetAddress1 = "Street address is required";
        } else {
          delete newErrors.streetAddress1;
        }
        break;
      case "city":
        if (!value.trim()) {
          newErrors.city = "City is required";
        } else {
          delete newErrors.city;
        }
        break;
      case "state":
        if (!value) {
          newErrors.state = "State is required";
        } else {
          delete newErrors.state;
        }
        break;
      case "zipCode":
        if (!value.trim()) {
          newErrors.zipCode = "Zip code is required";
        } else if (!isValidZipCode(value)) {
          newErrors.zipCode = "Invalid zip code (use 12345 or 12345-6789)";
        } else {
          delete newErrors.zipCode;
        }
        break;
      case "phoneNumber":
        if (!value.trim()) {
          newErrors.phoneNumber = "Phone number is required";
        } else if (!isValidPhoneNumber(value)) {
          newErrors.phoneNumber = "Invalid phone number (use 10 digits)";
        } else {
          delete newErrors.phoneNumber;
        }
        break;
      case "password":
        if (!value) {
          newErrors.password = "Password is required";
        } else if (!isValidPassword(value)) {
          newErrors.password = "Password must have 8+ characters, 1 uppercase, 1 lowercase, 1 number";
        } else {
          delete newErrors.password;
        }
        // Also check password2 match
        if (formData.password2 && value !== formData.password2) {
          newErrors.password2 = "Passwords do not match";
        } else if (formData.password2 && value === formData.password2) {
          delete newErrors.password2;
        }
        break;
      case "password2":
        if (!value) {
          newErrors.password2 = "Please confirm your password";
        } else if (value !== formData.password) {
          newErrors.password2 = "Passwords do not match";
        } else {
          delete newErrors.password2;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (touchedFields[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (name) => {
    setTouchedFields({ ...touchedFields, [name]: true });
    validateField(name, formData[name]);
  };

  const validateAllFields = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.streetAddress1.trim()) newErrors.streetAddress1 = "Street address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "Zip code is required";
    } else if (!isValidZipCode(formData.zipCode)) {
      newErrors.zipCode = "Invalid zip code (use 12345 or 12345-6789)";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Invalid phone number (use 10 digits)";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!isValidPassword(formData.password)) {
      newErrors.password = "Password must have 8+ characters, 1 uppercase, 1 lowercase, 1 number";
    }

    if (!formData.password2) {
      newErrors.password2 = "Please confirm your password";
    } else if (formData.password !== formData.password2) {
      newErrors.password2 = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Mark all fields as touched
    const allFields = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouchedFields(allFields);

    if (!validateAllFields()) {
      toast.error("Please fix all errors before submitting");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the Terms and Conditions");
      return;
    }

    setIsSubmitting(true);

    try {
      const { password2, ...formDataWithoutPassword2 } = formData;
      const response = await fetch(`${apiUrl}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formDataWithoutPassword2),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("auth", JSON.stringify(data));
        toast.success("Registration successful! Welcome aboard!");
        navigate("/login", { replace: true });
      } else {
        setIsSubmitting(false);
        toast.error(data.message || "Registration failed");
        if (data.message && data.message.includes("email")) {
          setErrors({ ...errors, email: "This email is already registered" });
        }
      }
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      toast.error("Registration unsuccessful. Please try again.");
    }
  };

  const getFieldClassName = (fieldName) => {
    if (!touchedFields[fieldName]) return "form-control form-control-lg";
    if (errors[fieldName]) return "form-control form-control-lg is-invalid";
    if (formData[fieldName]) return "form-control form-control-lg is-valid";
    return "form-control form-control-lg";
  };

  return (
      <>
        <Header />
        <div className="signup-page">
          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-lg-7 col-md-9">
                <div className="signup-card">
                  {/* Logo */}
                  <div className="text-center mb-4">
                    <img src={logo} alt="Logo" className="signup-logo" />
                  </div>

                  {/* Welcome Text */}
                  <div className="text-center mb-4">
                    <h2 className="signup-title">Create Your Account</h2>
                    <p className="signup-subtitle">Join us and start shopping today!</p>
                  </div>

                  {/* Signup Form */}
                  <form onSubmit={handleSubmit}>
                    {/* Personal Information Section */}
                    <div className="form-section">
                      <h5 className="section-title">
                        <PersonIcon className="me-2" />
                        Personal Information
                      </h5>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="firstName" className="form-label">First Name</label>
                          <input
                              type="text"
                              className={getFieldClassName("firstName")}
                              id="firstName"
                              name="firstName"
                              placeholder="John"
                              value={formData.firstName}
                              onChange={handleChange}
                              onBlur={() => handleBlur("firstName")}
                              disabled={isSubmitting}
                          />
                          {touchedFields.firstName && errors.firstName && (
                              <div className="invalid-feedback d-block">
                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                {errors.firstName}
                              </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label htmlFor="lastName" className="form-label">Last Name</label>
                          <input
                              type="text"
                              className={getFieldClassName("lastName")}
                              id="lastName"
                              name="lastName"
                              placeholder="Doe"
                              value={formData.lastName}
                              onChange={handleChange}
                              onBlur={() => handleBlur("lastName")}
                              disabled={isSubmitting}
                          />
                          {touchedFields.lastName && errors.lastName && (
                              <div className="invalid-feedback d-block">
                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                {errors.lastName}
                              </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          <EmailIcon fontSize="small" className="me-1" />
                          Email Address
                        </label>
                        <input
                            type="email"
                            className={getFieldClassName("email")}
                            id="email"
                            name="email"
                            placeholder="john.doe@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur("email")}
                            disabled={isSubmitting}
                        />
                        {touchedFields.email && errors.email && (
                            <div className="invalid-feedback d-block">
                              <ErrorOutlineIcon fontSize="small" className="me-1" />
                              {errors.email}
                            </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="phoneNumber" className="form-label">
                          <PhoneIcon fontSize="small" className="me-1" />
                          Phone Number
                        </label>
                        <input
                            type="tel"
                            className={getFieldClassName("phoneNumber")}
                            id="phoneNumber"
                            name="phoneNumber"
                            placeholder="1234567890"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            onBlur={() => handleBlur("phoneNumber")}
                            disabled={isSubmitting}
                            maxLength="10"
                        />
                        {touchedFields.phoneNumber && errors.phoneNumber && (
                            <div className="invalid-feedback d-block">
                              <ErrorOutlineIcon fontSize="small" className="me-1" />
                              {errors.phoneNumber}
                            </div>
                        )}
                      </div>
                    </div>

                    {/* Address Section */}
                    <div className="form-section">
                      <h5 className="section-title">
                        <HomeIcon className="me-2" />
                        Shipping Address
                      </h5>

                      <div className="mb-3">
                        <label htmlFor="streetAddress1" className="form-label">Street Address</label>
                        <input
                            type="text"
                            className={getFieldClassName("streetAddress1")}
                            id="streetAddress1"
                            name="streetAddress1"
                            placeholder="123 Main Street"
                            value={formData.streetAddress1}
                            onChange={handleChange}
                            onBlur={() => handleBlur("streetAddress1")}
                            disabled={isSubmitting}
                        />
                        {touchedFields.streetAddress1 && errors.streetAddress1 && (
                            <div className="invalid-feedback d-block">
                              <ErrorOutlineIcon fontSize="small" className="me-1" />
                              {errors.streetAddress1}
                            </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="streetAddress2" className="form-label">
                          Apartment, Suite, etc. <small className="text-muted">(Optional)</small>
                        </label>
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            id="streetAddress2"
                            name="streetAddress2"
                            placeholder="Apt 4B"
                            value={formData.streetAddress2}
                            onChange={handleChange}
                            disabled={isSubmitting}
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="city" className="form-label">City</label>
                          <input
                              type="text"
                              className={getFieldClassName("city")}
                              id="city"
                              name="city"
                              placeholder="New York"
                              value={formData.city}
                              onChange={handleChange}
                              onBlur={() => handleBlur("city")}
                              disabled={isSubmitting}
                          />
                          {touchedFields.city && errors.city && (
                              <div className="invalid-feedback d-block">
                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                {errors.city}
                              </div>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label htmlFor="state" className="form-label">State</label>
                          <select
                              className={getFieldClassName("state")}
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              onBlur={() => handleBlur("state")}
                              disabled={isSubmitting}
                          >
                            <option value="">Select State</option>
                            {states.map((state) => (
                                <option key={state} value={state}>
                                  {state}
                                </option>
                            ))}
                          </select>
                          {touchedFields.state && errors.state && (
                              <div className="invalid-feedback d-block">
                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                {errors.state}
                              </div>
                          )}
                        </div>

                        <div className="col-md-2 mb-3">
                          <label htmlFor="zipCode" className="form-label">Zip</label>
                          <input
                              type="text"
                              className={getFieldClassName("zipCode")}
                              id="zipCode"
                              name="zipCode"
                              placeholder="12345"
                              value={formData.zipCode}
                              onChange={handleChange}
                              onBlur={() => handleBlur("zipCode")}
                              disabled={isSubmitting}
                              maxLength="10"
                          />
                          {touchedFields.zipCode && errors.zipCode && (
                              <div className="invalid-feedback d-block">
                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                {errors.zipCode}
                              </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Password Section */}
                    <div className="form-section">
                      <h5 className="section-title">
                        <LockIcon className="me-2" />
                        Create Password
                      </h5>

                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <div className="password-input-wrapper">
                          <input
                              type={showPassword ? "text" : "password"}
                              className={getFieldClassName("password")}
                              id="password"
                              name="password"
                              placeholder="Enter password"
                              value={formData.password}
                              onChange={handleChange}
                              onBlur={() => handleBlur("password")}
                              disabled={isSubmitting}
                          />
                          <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex="-1"
                          >
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </button>
                        </div>
                        {touchedFields.password && errors.password && (
                            <div className="invalid-feedback d-block">
                              <ErrorOutlineIcon fontSize="small" className="me-1" />
                              {errors.password}
                            </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="password2" className="form-label">Confirm Password</label>
                        <div className="password-input-wrapper">
                          <input
                              type={showPassword2 ? "text" : "password"}
                              className={getFieldClassName("password2")}
                              id="password2"
                              name="password2"
                              placeholder="Confirm password"
                              value={formData.password2}
                              onChange={handleChange}
                              onBlur={() => handleBlur("password2")}
                              disabled={isSubmitting}
                          />
                          <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => setShowPassword2(!showPassword2)}
                              tabIndex="-1"
                          >
                            {showPassword2 ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </button>
                        </div>
                        {touchedFields.password2 && errors.password2 && (
                            <div className="invalid-feedback d-block">
                              <ErrorOutlineIcon fontSize="small" className="me-1" />
                              {errors.password2}
                            </div>
                        )}
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="form-check mb-4">
                      <input
                          type="checkbox"
                          className="form-check-input"
                          id="terms"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="terms">
                        I accept the{" "}
                        <Link to="/terms&conditions" target="_blank" className="terms-link">
                          Terms and Conditions
                        </Link>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="btn signup-button w-100 mb-3"
                        disabled={!termsAccepted || isSubmitting}
                    >
                      {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Creating Account...
                          </>
                      ) : (
                          <>
                            <HowToRegIcon className="me-2" />
                            Create Account
                          </>
                      )}
                    </button>

                    {/* Login Link */}
                    <div className="text-center">
                      <p className="mb-0 text-muted">
                        Already have an account?{" "}
                        <Link to="/login" className="login-link">
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
  );
};

export default SignupPage;